import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  TextField,
  Typography,
  fontStacks,
  useConfirm,
  useSnackbar,
} from '@munifocus/ui';
import type { ShiftHandoverDto } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import {
  useAcknowledgeHandover,
  useCancelHandover,
  useHandover,
  useHandovers,
} from '../lib/teamwork-api';
import { formatPeriod, handoverStatus } from '../components/teamwork/handover-display';
import { HandoverStatusChip } from '../components/teamwork/HandoverStatusChip';
import { WriteHandoverDialog } from '../components/teamwork/WriteHandoverDialog';
import { frenchFirstOfMonth } from '@munifocus/utils';

function HandoverSummary({ handover }: { handover: ShiftHandoverDto }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/handovers/${handover.id}`)}
      sx={{
        display: 'block',
        width: '100%',
        appearance: 'none',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        borderBottom: `1px solid ${palette.border.line}`,
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {handover.direction === 'sent'
            ? t('gov.teamwork.handovers.toName', { name: handover.to.name })
            : t('gov.teamwork.handovers.fromName', { name: handover.from.name })}
        </Typography>
        <HandoverStatusChip handover={handover} />
      </Stack>
      <Typography variant="caption" sx={{ color: palette.text.muted, display: 'block' }}>
        {formatPeriod(handover.periodStart, handover.periodEnd, i18n.language)}
        {handover.files.length > 0 &&
          ` · ${t('gov.teamwork.handovers.fileCount', { count: handover.files.length })}`}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: palette.text.secondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {handover.note}
      </Typography>
    </Box>
  );
}

/** Handovers written to and by the officer, and the way to write one. */
export function Handovers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useHandovers();
  const [open, setOpen] = useState(false);

  return (
    <AppShell>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 1, mb: 2 }}
      >
        <Box>
          {/* The app bar carries the page title from md up (as on Settings); a
              second heading with the same name there is noise to a screen reader. */}
          <Stack direction="row" spacing={1} sx={{ display: { md: 'none' }, alignItems: 'center' }}>
            <Icons.ArrowRightLeft size={iconSize.lg} aria-hidden />
            <Typography variant="h5">{t('gov.teamwork.handovers.title')}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('gov.teamwork.handovers.subtitle')}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setOpen(true)}>
          {t('gov.teamwork.handovers.write')}
        </Button>
      </Stack>

      {list.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} aria-label={t('gov.teamwork.handovers.loading')} />
        </Box>
      )}
      {list.isError && <Alert severity="error">{t('gov.teamwork.handovers.loadError')}</Alert>}

      {list.data && (
        <Stack spacing={2}>
          {(['received', 'sent'] as const).map((side) => (
            <HairlineCard key={side} sx={{ p: 0, overflow: 'hidden' }}>
              <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, alignItems: 'center' }}>
                <Typography variant="overline">
                  {t(
                    side === 'received'
                      ? 'gov.teamwork.handovers.received'
                      : 'gov.teamwork.handovers.sentList',
                  )}
                </Typography>
                {side === 'received' && list.data.pending > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: palette.brand.accent, fontWeight: 700 }}
                  >
                    {t('gov.teamwork.handovers.pendingCount', { count: list.data.pending })}
                  </Typography>
                )}
              </Stack>
              {list.data[side].length === 0 ? (
                <Typography variant="body2" sx={{ color: palette.text.muted, px: 2, pb: 2 }}>
                  {t(`gov.teamwork.handovers.${side}Empty`)}
                </Typography>
              ) : (
                list.data[side].map((h) => <HandoverSummary key={h.id} handover={h} />)
              )}
            </HairlineCard>
          ))}
        </Stack>
      )}

      <WriteHandoverDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(id) => navigate(`/handovers/${id}`)}
      />
    </AppShell>
  );
}

/** One handover: the note, its files, and acknowledging or withdrawing it. */
export function HandoverDetail() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  const handover = useHandover(id);
  const acknowledge = useAcknowledgeHandover();
  const cancel = useCancelHandover();
  const [ackNote, setAckNote] = useState('');
  const h = handover.data;
  const status = h ? handoverStatus(h) : null;

  return (
    <AppShell
      back={{ label: t('gov.teamwork.handovers.title'), onClick: () => navigate('/handovers') }}
    >
      {handover.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} aria-label={t('gov.teamwork.handovers.loading')} />
        </Box>
      )}
      {handover.isError && <Alert severity="error">{t('gov.teamwork.handovers.notFound')}</Alert>}
      {h && (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5">
              {t('gov.teamwork.handovers.fromTo', { from: h.from.name, to: h.to.name })}
            </Typography>
            <HandoverStatusChip handover={h} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {formatPeriod(h.periodStart, h.periodEnd, i18n.language)}
          </Typography>

          <HairlineCard sx={{ p: 2 }}>
            <Typography variant="overline">{t('gov.teamwork.handovers.note')}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {h.note}
            </Typography>
          </HairlineCard>

          {h.files.length > 0 && (
            <HairlineCard sx={{ p: 0, overflow: 'hidden' }}>
              <Typography variant="overline" sx={{ display: 'block', px: 2, pt: 1.5 }}>
                {t('gov.teamwork.handovers.files')}
              </Typography>
              {h.files.map((f) => (
                <Box
                  key={f.fileId}
                  component="button"
                  type="button"
                  onClick={() => navigate(`/files/${f.fileId}`)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    appearance: 'none',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    borderTop: `1px solid ${palette.border.line}`,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: fontStacks.mono, fontWeight: 700 }}>
                    {f.reference}
                  </Typography>
                  <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                    {f.address}
                  </Typography>
                  {f.note && <Typography variant="body2">{f.note}</Typography>}
                </Box>
              ))}
            </HairlineCard>
          )}

          {h.acknowledgedAt && (
            <Alert severity="success">
              {t('gov.teamwork.handovers.acknowledgedOn', {
                name: h.to.name,
                when: frenchFirstOfMonth(
                  new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(h.acknowledgedAt)),
                ),
              })}
              {h.acknowledgementNote ? ` — ${h.acknowledgementNote}` : ''}
            </Alert>
          )}

          {h.direction === 'received' && status === 'pending' && (
            <HairlineCard sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  label={t('gov.teamwork.handovers.ackNote')}
                  value={ackNote}
                  onChange={(e) => setAckNote(e.target.value.slice(0, 1000))}
                  multiline
                  minRows={2}
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<Icons.Check size={iconSize.md} />}
                    disabled={acknowledge.isPending}
                    onClick={() =>
                      acknowledge.mutate(
                        { id: h.id, ...(ackNote.trim() ? { note: ackNote.trim() } : {}) },
                        {
                          onSuccess: () =>
                            snackbar.success(t('gov.teamwork.handovers.acknowledged')),
                        },
                      )
                    }
                  >
                    {t('gov.teamwork.handovers.acknowledge')}
                  </Button>
                </Box>
              </Stack>
            </HairlineCard>
          )}

          {h.direction === 'sent' && status === 'pending' && (
            <Box>
              <Button
                color="error"
                variant="outlined"
                disabled={cancel.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: t('gov.teamwork.handovers.withdrawTitle'),
                    body: t('gov.teamwork.handovers.withdrawBody', { name: h.to.name }),
                    confirmLabel: t('gov.teamwork.handovers.withdraw'),
                    cancelLabel: t('common.actions.cancel'),
                    tone: 'danger',
                  });
                  if (ok) cancel.mutate(h.id);
                }}
              >
                {t('gov.teamwork.handovers.withdraw')}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </AppShell>
  );
}
